import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Brain, FileText, ChevronDown, ChevronRight, FolderOpen, Bell, MessageSquare, Zap, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { format } from 'date-fns';
import BotMemoryReviewDialog from './BotMemoryReviewDialog';
import BotSkillsSection from './BotSkillsSection';

export default function BotMemorySection({ profile, currentUserEmail, isOwner, canManageSkills = false, autoOpenReview = false }) {
  const queryClient = useQueryClient();
  const [expandedNotes, setExpandedNotes] = React.useState({});
  const [reviewDialogOpen, setReviewDialogOpen] = React.useState(false);
  const [selectedFeedback, setSelectedFeedback] = React.useState(null);
  const pendingReviewCardRef = React.useRef(null);
  const [memorySectionOpen, setMemorySectionOpen] = React.useState(false);
  const [dailyNotesSectionOpen, setDailyNotesSectionOpen] = React.useState(false);
  const [unprocessedOpen, setUnprocessedOpen] = React.useState(false);

  // Fetch pending owner feedback
  const { data: pendingFeedback } = useQuery({
    queryKey: ['pendingFeedback', profile?.id],
    queryFn: async () => {
      const memories = await base44.entities.BotMemory.filter({
        bot_profile_id: profile.id,
        type: 'OWNER_FEEDBACK',
        owner_review_status: 'pending'
      }, '-date', 10);
      return memories || [];
    },
    enabled: !!profile?.id && isOwner
  });

  // Auto-scroll to pending review card when coming from dashboard
  React.useEffect(() => {
    if (autoOpenReview && pendingFeedback?.length > 0 && pendingReviewCardRef.current) {
      setTimeout(() => {
        pendingReviewCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [autoOpenReview, pendingFeedback]);

  // Fetch memory summary
  const { data: memorySummary } = useQuery({
    queryKey: ['botMemory', 'MEMORY_SUMMARY', profile?.id],
    queryFn: async () => {
      const memories = await base44.entities.BotMemory.filter({
        bot_profile_id: profile.id,
        type: 'MEMORY_SUMMARY'
      }, '-date', 1);
      return memories[0] || null;
    },
    enabled: !!profile?.id
  });

  // Fetch daily notes
  const { data: dailyNotes } = useQuery({
    queryKey: ['botMemory', 'DAILY_NOTE', profile?.id],
    queryFn: async () => {
      const memories = await base44.entities.BotMemory.filter({
        bot_profile_id: profile.id,
        type: 'DAILY_NOTE'
      }, '-date', 30);
      return memories || [];
    },
    enabled: !!profile?.id
  });

  // Fetch unprocessed WORKING_TASKs (tasks that haven't been aggregated into daily notes yet)
  const { data: unprocessedTasks = [] } = useQuery({
    queryKey: ['botMemory', 'WORKING_TASK_UNPROCESSED', profile?.id],
    queryFn: async () => {
      const workingTasks = await base44.entities.BotMemory.filter({
        bot_profile_id: profile.id,
        type: 'WORKING_TASK'
      }, '-date', 50);
      // A task is "unprocessed" if there's no DAILY_NOTE for its date
      const processedDates = new Set((dailyNotes || []).map(n => n.date));
      return workingTasks.filter(t => !processedDates.has(t.date));
    },
    enabled: !!profile?.id && isOwner
  });

  // Group unprocessed tasks by date for display
  const unprocessedByDate = React.useMemo(() => {
    const grouped = {};
    for (const task of unprocessedTasks) {
      if (!grouped[task.date]) grouped[task.date] = [];
      grouped[task.date].push(task);
    }
    return grouped;
  }, [unprocessedTasks]);
  const unprocessedDates = Object.keys(unprocessedByDate).sort().reverse();

  // Process Learnings mutation — processes today + yesterday
  const processLearningsMutation = useMutation({
    mutationFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      // Process all dates that have unprocessed tasks
      const datesToProcess = unprocessedDates.length > 0 ? unprocessedDates : [today, yesterday];
      const results = [];
      for (const date of datesToProcess) {
        try {
          const res = await base44.functions.invoke('processBotDailyMemory', { date_override: date });
          results.push({ date, ...res.data });
        } catch (err) {
          results.push({ date, error: err.message });
        }
      }
      return results;
    },
    onSuccess: () => {
      // Refresh all memory queries
      queryClient.invalidateQueries({ queryKey: ['botMemory'] });
      queryClient.invalidateQueries({ queryKey: ['pendingFeedback'] });
      queryClient.invalidateQueries({ queryKey: ['pendingBotMemoryReviews'] });
    }
  });

  const toggleNote = (noteId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  const openReviewDialog = (feedback) => {
    setSelectedFeedback(feedback);
    setReviewDialogOpen(true);
  };

  return (
    <>
    {/* Pending Feedback Alert for Owner */}
    {isOwner && pendingFeedback?.length > 0 && (
      <Card ref={pendingReviewCardRef} className="glass-panel border-none border-l-4 border-l-amber-500 mb-4">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Bell className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h4 className="text-slate-700 font-semibold">Pending Memory Review</h4>
                <p className="text-sm text-slate-500">
                  {pendingFeedback.length} learning{pendingFeedback.length > 1 ? 's' : ''} waiting for your review
                </p>
              </div>
            </div>
            <Button
              onClick={() => openReviewDialog(pendingFeedback[0])}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Review Now
            </Button>
          </div>
        </CardContent>
      </Card>
    )}
    {/* Process Learnings Button for Owner */}
    {isOwner && unprocessedTasks.length > 0 && (
      <Card className="glass-panel border-none border-l-4 border-l-indigo-500 mb-4">
        <CardContent className="py-4">
          <Collapsible open={unprocessedOpen} onOpenChange={setUnprocessedOpen}>
            <div className="flex items-center justify-between">
              <CollapsibleTrigger className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
                <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-indigo-400" />
                </div>
                <div className="min-w-0 text-left">
                  <div className="flex items-center gap-2">
                    {unprocessedOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                    <h4 className="text-slate-700 font-semibold">Unprocessed Work</h4>
                  </div>
                  <p className="text-sm text-slate-500 break-words">
                    {unprocessedTasks.length} task{unprocessedTasks.length !== 1 ? 's' : ''} across {unprocessedDates.length} day{unprocessedDates.length !== 1 ? 's' : ''} — ready to consolidate
                  </p>
                </div>
              </CollapsibleTrigger>
              <Button
                onClick={(e) => { e.stopPropagation(); processLearningsMutation.mutate(); }}
                disabled={processLearningsMutation.isPending}
                className="bg-indigo-600 hover:bg-indigo-700 text-white flex-shrink-0 ml-3"
              >
                {processLearningsMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                ) : (
                  <><Zap className="w-4 h-4 mr-2" />Process Learnings</>
                )}
              </Button>
            </div>
            <CollapsibleContent>
              <div className="mt-3 space-y-3">
                {unprocessedDates.map(date => (
                  <div key={date} className="space-y-1">
                    <p className="text-xs font-semibold text-slate-600">{date}</p>
                    {unprocessedByDate[date].map(task => (
                      <div key={task.id} className="bg-slate-50 p-3 rounded-lg border border-slate-200 prose prose-sm prose-slate max-w-none break-words overflow-hidden">
                        <ReactMarkdown>{task.content}</ReactMarkdown>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
          {processLearningsMutation.isSuccess && (
            <div className="mt-3 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <p className="text-xs text-emerald-600">
                ✓ Learnings processed. Check Daily Notes and Pending Memory Review above.
              </p>
            </div>
          )}
          {processLearningsMutation.isError && (
            <div className="mt-3 p-2 bg-rose-500/10 border border-rose-500/20 rounded-lg">
              <p className="text-xs text-rose-400">
                Error: {processLearningsMutation.error?.message || 'Failed to process'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    )}
    <Card className="glass-panel border-none">
      <CardHeader>
        <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
          <Brain className="w-5 h-5 text-pink-400" />
          Memory & Skills
        </CardTitle>
        <p className="text-xs text-slate-500 mt-2">Bot's accumulated knowledge, daily notes, and domain skills</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Memory Summary (MEMORY.md) */}
        <Collapsible open={memorySectionOpen} onOpenChange={setMemorySectionOpen}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
              {memorySectionOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
              <FileText className="w-4 h-4 text-pink-400" />
              <h4 className="text-slate-700 text-sm font-semibold uppercase tracking-wider">MEMORY.md</h4>
              {memorySummary && (
                <Badge variant="outline" className="text-xs border-pink-500/30 text-pink-400">
                  Updated {format(new Date(memorySummary.date), 'MMM d, yyyy')}
                </Badge>
              )}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2">
              {memorySummary ? (
                <div className="bg-slate-100 p-4 rounded-lg border border-slate-800 prose prose-sm prose-slate max-w-none break-words overflow-hidden">
                  <ReactMarkdown>{memorySummary.content}</ReactMarkdown>
                </div>
              ) : (
                <div className="bg-slate-100 p-4 rounded-lg border border-slate-800 text-center text-slate-500 text-sm">
                  No memory summary yet. Memory will be populated as the bot learns.
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Daily Notes Directory */}
        <Collapsible open={dailyNotesSectionOpen} onOpenChange={setDailyNotesSectionOpen}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
              {dailyNotesSectionOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
              <FolderOpen className="w-4 h-4 text-amber-400" />
              <h4 className="text-slate-700 text-sm font-semibold uppercase tracking-wider">Daily Notes</h4>
              <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">
                {dailyNotes?.length || 0} entries
              </Badge>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2">
              {dailyNotes?.length > 0 ? (
                <div className="space-y-2">
                  {dailyNotes.map((note) => (
                    <Collapsible 
                      key={note.id} 
                      open={expandedNotes[note.id]}
                      onOpenChange={() => toggleNote(note.id)}
                    >
                      <CollapsibleTrigger className="w-full">
                        <div className="bg-slate-100 p-3 rounded-lg border border-slate-800 flex items-center justify-between hover:bg-slate-200/50 transition-colors cursor-pointer">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-500" />
                            <span className="text-slate-700 text-sm font-mono">
                              {format(new Date(note.date), 'yyyy-MM-dd')}.md
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">
                              {format(new Date(note.date), 'EEEE')}
                            </span>
                            {expandedNotes[note.id] ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )}
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="bg-slate-50 p-4 rounded-b-lg border border-t-0 border-slate-800 prose prose-sm prose-slate max-w-none break-words overflow-hidden">
                          <ReactMarkdown>{note.content}</ReactMarkdown>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-100 p-4 rounded-lg border border-slate-800 text-center text-slate-500 text-sm">
                  No daily notes yet. Notes will be recorded as the bot completes tasks.
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Skills.md Section */}
        <BotSkillsSection profile={profile} isOwner={isOwner} canManageSkills={canManageSkills} />
      </CardContent>
    </Card>

      {/* Memory Review Dialog */}
      <BotMemoryReviewDialog
        open={reviewDialogOpen}
        onOpenChange={setReviewDialogOpen}
        feedbackEntry={selectedFeedback}
        botProfile={profile}
      />
    </>
  );
}